import {
  useGetMillwrightDaysSinceLastFatigueWarningQuery,
  useGetMillwrightDaysSinceLastIncidentQuery,
} from '@/apis/incidents/incidentsApi';
import { useGetMillwrightWorkedHoursQuery } from '@/apis/workedHours/workedHoursApi';
import { FC } from 'react';
import { GradientBar } from '@/components';
import {
  Icon,
  LineChart,
  LoadingSpinner,
  Stack,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { SPINNER_SIZE } from '@/constants';
import {
  MillwrightLastAccident,
  MillwrightLastFatigueWarning,
} from '@/pages/Millwrights/[ID]/components';
import {
  dtRanges,
  otRanges,
  rtRanges,
} from '@/pages/Millwrights/[ID]/components/MillwrightProfile/kpisData';
import { useGetMillwrightRiskTimelineByOutageIdQuery } from '@/apis/risks/risksApi';
import { RangeColor } from '@/types';

import '@/pages/Millwrights/[ID]/components/MillwrightProfile/MillwrightProfile.scss';

const TIMELINE_TOOLTIP_INFO = `This chart displays the worker’s injury risk score trend over the past six months, based on key factors such as age and worked hours.

Each point on the line represents the calculated risk score for that period. Changes in the trend may reflect variations in workload, overtime, double-time hours, or recovery time.

Injury events, if any occurred during this period, are marked directly on the timeline. These indicators show when an injury was recorded, helping correlate risk score changes with real incidents.

Use this chart to identify rising risk patterns, periods of elevated strain, and potential early warning signs before injuries occur.`;

interface MillwrightProfileProps {
  millwrightId: number;
  outageId?: number;
}

export const MillwrightProfile: FC<MillwrightProfileProps> = ({
  millwrightId,
  outageId,
}) => {
  const { data: workedHours, isLoading: isWorkedHoursLoading } =
    useGetMillwrightWorkedHoursQuery(
      {
        empId: millwrightId,
        outageId,
      },
      { skip: !millwrightId || !outageId },
    );
  const { data: daysSinceLastIncident, isLoading: isDaysSinceIncidentLoading } =
    useGetMillwrightDaysSinceLastIncidentQuery(millwrightId, {
      skip: !millwrightId,
    });
  const {
    data: daysSinceLastWarning,
    isLoading: isDaysSinceLastWarningLoading,
  } = useGetMillwrightDaysSinceLastFatigueWarningQuery(millwrightId, {
    skip: !millwrightId,
  });
  const { data: riskTimeline, isLoading: isRiskTimelineLoading } =
    useGetMillwrightRiskTimelineByOutageIdQuery(
      {
        empId: millwrightId,
        months: 6,
      },
      { skip: !millwrightId },
    );

  const isLoading =
    isWorkedHoursLoading ||
    isDaysSinceIncidentLoading ||
    isDaysSinceLastWarningLoading ||
    isRiskTimelineLoading;

  if (isLoading) {
    return (
      <div className="millwright-profile">
        <LoadingSpinner
          text="Loading millwright profile..."
          size={SPINNER_SIZE}
          color="var(--color-primary)"
          align="center"
          tag="textdefault"
          inline
        />
      </div>
    );
  }

  return (
    <div className="millwright-profile">
      <Stack gap={48}>
        <Typography color="var(--color-black)" tag="h3">
          Millwright Profile
        </Typography>
        <div className="millwright-profile__data">
          <div className="millwright-profile__bars">
            {workedHours?.rt_sum_6m !== undefined && (
              <GradientBar
                value={workedHours.rt_sum_6m ?? 0}
                label="Regular hours (6m)"
                data={rtRanges as RangeColor[]}
                description="Total number of standard (non-overtime) hours worked over the past six months. This metric helps assess overall workload exposure and baseline physical demand."
              />
            )}
            {workedHours?.dt_sum_6m !== undefined && (
              <GradientBar
                value={workedHours.dt_sum_6m ?? 0}
                label="Double hours (6m)"
                data={dtRanges as RangeColor[]}
                description="Total number of double-time hours worked during the past six months. Sustained high-intensity or extended shifts may significantly increase physical and mental strain, contributing to higher injury risk."
              />
            )}
            {workedHours?.ot_sum_6m !== undefined && (
              <GradientBar
                value={workedHours.ot_sum_6m ?? 0}
                label="Overtime hours (6m)"
                data={otRanges as RangeColor[]}
                description="Total number of overtime hours worked in the past six months. Extended work hours can increase fatigue, reduce alertness, and elevate the risk of errors or injury."
              />
            )}
          </div>
          <div className="millwright-profile__risk-timeline">
            <Stack direction="row" gap={8}>
              <Typography tag="textdefault_strong" color="var(--color-neutral)">
                Risk score timeline (6m)
              </Typography>
              <Tooltip title={TIMELINE_TOOLTIP_INFO} arrow>
                <Icon
                  icon="IconInformation"
                  color="var(--color-default)"
                  iconSize={0.8}
                />
              </Tooltip>
            </Stack>

            <LineChart
              data={
                riskTimeline?.points.map((item) => ({
                  ...item,
                  name: item.date,
                })) ?? []
              }
              categoryKey="name"
              chartKeys={['riskScore', 'injury']}
              hasAnimation
              hasGrid
              hasTooltip
              hasBrush={false}
              legendType="none"
              height={420}
            />
          </div>
        </div>

        <div className="millwright-profile__incidents">
          <MillwrightLastAccident data={daysSinceLastIncident} />
          <MillwrightLastFatigueWarning data={daysSinceLastWarning} />
        </div>
      </Stack>
    </div>
  );
};
